import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/molecules/Card';
import { KaraokePlayer } from '@/components/molecules/KaraokePlayer';
import { FileText, Headphones, Video } from 'lucide-react';
import { Icon } from '@/components/atoms/Icon';
import { cn } from '@/utils/cn';
import { formatDuration } from '@/utils/formatTime';
import type { Resource } from '@/types/story.types';
import styles from '../StoryPage.module.scss';

interface ResourcesTabProps {
  resources: Resource[];
  onAudioFinished?: () => void;
  /** Reproduce el audio karaoke automáticamente al montar (flujo unificado de reto post-«Empezar»). */
  audioAutoPlay?: boolean;
  /** El alumno ya escuchó el audio completo en una sesión anterior: arranca en estado «terminado». */
  audioInitiallyListened?: boolean;
}

function getResourceIcon(type: Resource['type']) {
  switch (type) {
    case 'text':
      return FileText;
    case 'audio':
      return Headphones;
    case 'video':
      return Video;
    default:
      return FileText;
  }
}

export function ResourcesTab({
  resources,
  onAudioFinished,
  audioAutoPlay = false,
  audioInitiallyListened = false,
}: ResourcesTabProps) {
  const { t } = useTranslation();

  if (resources.length === 0) {
    return (
      <div className={styles.story__empty}>
        <Icon icon={FileText} size="lg" />
        <p className={styles.story__emptyText}>{t('resourcesTab.noResources')}</p>
        <p className={styles.story__emptySubtext}>{t('resourcesTab.noResourcesSub')}</p>
      </div>
    );
  }

  return (
    <div className={styles.story__resources}>
      {resources.map((resource) => (
        <Card
          key={resource.id}
          className={cn(
            styles.story__resourceCard,
            resource.type === 'audio' && styles['story__resourceCard--audio']
          )}
        >
          {/* No header for text — content only */}
          {resource.type !== 'audio' && resource.type !== 'text' && (
            <CardHeader>
              <CardTitle className={styles.story__resourceTitle}>
                <Icon icon={getResourceIcon(resource.type)} size="md" />
                {resource.title}
                {resource.duration && (
                  <span className={styles.story__resourceDuration}>
                    {formatDuration(resource.duration)}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
          )}
          <CardContent
            className={
              resource.type === 'audio' ? styles.story__audioContent : undefined
            }
          >
            {resource.type === 'text' && (
              <div className={styles.story__textContent}>
                <p>{resource.content}</p>
              </div>
            )}

            {resource.type === 'audio' && (
              <KaraokePlayer
                audioUrl={resource.audioUrl}
                transcriptUrl={resource.transcript}
                onAudioFinished={onAudioFinished}
                autoPlay={audioAutoPlay}
                initiallyListened={audioInitiallyListened}
              />
            )}

            {resource.type === 'video' && (
              <div className={styles.story__videoContent}>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video controls className={styles.story__videoPlayer}>
                  <source src={resource.videoUrl} type="video/mp4" />
                  {t('resourcesTab.videoNotSupported')}
                </video>
                {resource.transcript && (
                  <details className={styles.story__transcript}>
                    <summary>{t('resourcesTab.viewTranscript')}</summary>
                    <p>{resource.transcript}</p>
                  </details>
                )}
              </div>
            )}

            {resource.description && (
              <p className={styles.story__resourceDescription}>{resource.description}</p>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
